import { gql, useMutation } from "@apollo/client";
import { Theme } from "@mui/material";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { makeStyles } from "@mui/styles";
import Users from "components/users/Users";
import { RestrictedSolutionRealm } from "features/access-management";
import { useSolutionKey, useSolutionName } from "features/solution";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { notNullOrUndefined } from "utils/typescript";
import { createRoute } from "utils/url";
import {
  IScopedPermission__SolutionScope,
  ScopedRole__SolutionScope
} from "../../model/schema/globalTypes";
import {
  membersList,
  useQueryGetAvailableRoles,
  useQueryGetMembers
} from "./hooks/membersHooks";
import {
  RemoveSolutionMember,
  RemoveSolutionMemberVariables
} from "./schema/RemoveSolutionMember";
import {
  UpdateMemberRole,
  UpdateMemberRoleVariables
} from "./schema/UpdateMemberRole";

export const UpdateSolutionMemberRoleMutation = gql`
  mutation UpdateMemberRole(
    $solutionKey: Key!
    $roles: [ScopedRole__SolutionScope!]!
    $userName: String!
  ) {
    solutions {
      solution(solutionKey: $solutionKey) {
        access {
          updateUserRoles(userName: $userName, roles: $roles) {
            name
          }
        }
      }
    }
  }
`;
export const removeSolutionMemberMutation = gql`
  mutation RemoveSolutionMember($solutionKey: Key!, $userName: String!) {
    solutions {
      solution(solutionKey: $solutionKey) {
        access {
          removeSolutionMember(userName: $userName) {
            name
          }
        }
      }
    }
  }
`;

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    color: "#FFFFFF",
    marginLeft: theme.spacing(1),
    backgroundColor: theme.palette.primary.dark,
    textTransform: "none",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark
    }
  }
}));

export default function Members() {
  const classNames = useStyles();
  const history = useHistory();
  const solutionKey = useSolutionKey();
  const { data } = useSolutionName({ solutionKey });
  const solutionName = data?.solutions.solution?.name || solutionKey;

  const [
    loadMembers,
    { data: members, loading: loadingMembers, error: getMembersError }
  ] = useQueryGetMembers(solutionKey);
  const { data: roles, loading: loadingAvailableRoles } =
    useQueryGetAvailableRoles(solutionKey);

  const [
    updateMemberRole,
    {
      loading: updateMemberRoleLoading,
      error: updateMemberRoleError,
      reset: resetUpdateMemberRoleError
    }
  ] = useMutation<UpdateMemberRole, UpdateMemberRoleVariables>(
    UpdateSolutionMemberRoleMutation,
    {
      notifyOnNetworkStatusChange: true,
      awaitRefetchQueries: true,
      refetchQueries: [{ query: membersList, variables: { solutionKey } }]
    }
  );

  const [
    removeSolutionMember,
    {
      error: removeMemberError,
      reset: resetRemoveMemberError,
      loading: removeSolutionMemberLoading
    }
  ] = useMutation<RemoveSolutionMember, RemoveSolutionMemberVariables>(
    removeSolutionMemberMutation,
    {
      refetchQueries: [{ query: membersList, variables: { solutionKey } }]
    }
  );

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const allMembers = (members?.solutions.solution?.access?.users || []).filter(
    notNullOrUndefined
  );

  const allRoles = roles?.solutions.solution?.access?.availableRoles || [];

  const isLoading =
    loadingMembers ||
    loadingAvailableRoles ||
    updateMemberRoleLoading ||
    removeSolutionMemberLoading;

  const adminCount = allMembers.filter(m =>
    m.roles.includes(ScopedRole__SolutionScope.Admin)
  ).length;

  return (
    <Users<ScopedRole__SolutionScope>
      users={allMembers}
      allRoles={allRoles}
      loadingRoles={updateMemberRoleLoading}
      loadUsers={loadMembers}
      onRoleUpdated={(userName, roles) => {
        updateMemberRole({ variables: { solutionKey, userName, roles } });
      }}
      adminRole={ScopedRole__SolutionScope.Admin}
      adminCount={adminCount}
      updateUserRoleError={updateMemberRoleError}
      removeUserError={removeMemberError}
      resetRemoveUserError={resetRemoveMemberError}
      resetUpdateUserRoleError={resetUpdateMemberRoleError}
      onRemoveUser={userName => {
        removeSolutionMember({ variables: { solutionKey, userName } });
      }}
      isLoading={isLoading}
      error={getMembersError}
      title={`Members of ${solutionName}`}
      onRefresh={loadMembers}
      lastAdminWarning="You are attempting to remove the last admin from this solution. By dropping this role, the solution would no longer be maintainable. If you want to decommission the solution, use the archive function instead."
      headerActions={
        <RestrictedSolutionRealm
          requiredPermissions={[
            IScopedPermission__SolutionScope.ReadSolutionMembers,
            IScopedPermission__SolutionScope.InviteSolutionMember
          ]}
          silent
        >
          <Tooltip title={`Invite member to solution ${solutionName}`}>
            <div>
              <Button
                className={classNames.button}
                variant="contained"
                data-testid="add-user-button"
                onClick={() => {
                  history.push(
                    createRoute(`/solutions/${solutionKey}/members/invite`)
                  );
                }}
              >
                Invite member
              </Button>
            </div>
          </Tooltip>
        </RestrictedSolutionRealm>
      }
    />
  );
}
